import React, { useEffect, useState, useMemo } from 'react';
import cn from 'classnames';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import CareerCard, { ICareerCardProps } from '../CareerCard';
import Loading from 'tbk-components/src/components/Loading';
import { useHashAsState } from 'tbk-components/src/hooks';
import qs from 'qs';
import Select, { createFilter } from '../../common/ReactSelect';

export interface IFilterOptionProps {
	label: string;
	value: string;
	children?: IFilterOptionProps[];
}

export interface ICareersFilterProps {
	label: string;
	taxonomy: string;
	options: IFilterOptionProps[];
}

export interface ICareerCardGroupProps extends IBaseElementProps {
	/**
	 * Card Group ID
	 */
	cardGroupID?: string;
	/**
	 * Heading
	 */
	heading?: string;
	/**
	 * Show Filter
	 */
	showFilter?: boolean;
	filters?: ICareersFilterProps[];
	/**
	 * Show Results
	 */
	showResults?: boolean;
	/**
	 *  Cards to Show
	 */
	cardsToShow?: number;
	totalResults?: number;
	/**
	 * Load more button text
	 */
	loadMoreButtonText?: string;
	noResultsText?: string;
	intermediateResultsText?: [string, string];
	/**
	 * Career Cards
	 */
	calianCareerCards?: ICareerCardProps[];
	/**
	 * Card Summary Heading
	 */
	cardSummaryHeading?: string;
	busy?: boolean;
	loadingMore?: boolean;
	onShowMore?: () => void;
	onClickCareerTerm?: (taxonomy: string, value: string) => void;
}

/**
 * This is a recursive function that knows how to build out the options where the children get a little indentation.
 *
 * @param options
 * @param level
 * @returns
 */
export const buildfilterOptions = (
	options: IFilterOptionProps[],
	level = 0,
) => {
	let builtOptions = [];
	(options || []).forEach((filter) => {
		builtOptions.push(
			<option key={filter.value} value={filter.value}>
				{/* such ridiculousness to get the indentation right */}
				{[...Array(level).keys()].map((i) => (
					<React.Fragment key={i}>
						&nbsp;&nbsp;&nbsp;&nbsp;
					</React.Fragment>
				))}
				{filter.label}
			</option>,
		);

		if (filter.children) {
			builtOptions = builtOptions.concat(
				buildfilterOptions(filter.children, level + 1),
			);
		}
	});
	return builtOptions;
};

/**
 * This helper function exists to build out the options for a React Select component.
 * @param options
 * @param level
 * @returns
 */
export const buildfilterOptionsForReactSelect = (
	options: IFilterOptionProps[],
	level = 0,
) => {
	let builtOptions = {};
	(options || []).forEach((filter) => {
		if ('' === filter.value) {
			// for ReactSelect, we'll handle these as a placeholder
			return;
		}
		builtOptions[filter.value] = {
			value: filter.value,
			label: (
				<>
					{[...Array(level).keys()].map((i) => (
						<React.Fragment key={i}>
							&nbsp;&nbsp;&nbsp;&nbsp;
						</React.Fragment>
					))}
					{filter.label}
				</>
			),
			canonical: filter.label,
		};

		if (filter.children) {
			Object.assign(
				builtOptions,
				buildfilterOptionsForReactSelect(filter.children, level + 1),
			);
		}
	});
	return builtOptions;
};

export const memoizeOptionsForReactSelect = (
	filters: ICareersFilterProps[],
) => {
	const filterOptions = {};
	(filters || []).forEach((filter) => {
		filterOptions[filter.taxonomy] = buildfilterOptionsForReactSelect(
			filter.options,
		);
	});
	// Because we're building <React.Fragment> elements for the label, we need another way to search
	// based on the label.  To make that happen, we have added a "canonical" property to the option, and
	// this searchConfigs object can be used to tell the ReactSelect component how to search.
	const searchConfig = createFilter({
		ignoreAccents: true,
		ignoreCase: true,
		matchFrom: 'any',
		trim: true,
		stringify: (o: any) => o.data.canonical,
	});
	return [ filterOptions, searchConfig ];
};

/**
 * Calian Career Card Group Component
 */
const CareerCardGroup: React.FC<ICareerCardGroupProps> = ({
	className,
	classNames = [],
	cardGroupID = 'calian-career-group',
	heading,
	showFilter,
	showResults,
	filters,
	cardsToShow = 5,
	totalResults,
	loadMoreButtonText = 'Load more jobs',
	noResultsText = 'No results found.',
	intermediateResultsText = ['Showing %1 of %2 job', 'Showing %1 of %2 jobs'],
	calianCareerCards,
	cardSummaryHeading,
	busy = false,
	loadingMore = false,
	onShowMore,
	onClickCareerTerm,
}) => {
	const dynamicParameters = {};
	(filters || []).forEach((filter) => {
		dynamicParameters[filter.taxonomy] = 'string';
	});

	const [_cardSummaryHeading, setCardSummaryHeading] =
		useState<string>(cardSummaryHeading);

	const [hashedState, normalizedState, updateState] = useHashAsState({
		parameters: {
			...dynamicParameters,
		},
		defaults: {},
	});

	const state = useMemo(() => {
		const state = qs.parse(normalizedState.replace(/^#!/, ''));
		if (Object.entries(state).length) {
			return state;
		} else {
			return null;
		}
	}, [normalizedState]);
	const [ filterOptions, searchConfig ] = useMemo(
		() => memoizeOptionsForReactSelect(filters),
		[filters],
	);

	const [cardsToDisplay, setCardsToDisplay] =
		useState<ICareerCardProps[]>(null);
	useEffect(() => {
		if (showFilter && state) {
			const count = cardsToDisplay?.length.toString();
			setCardSummaryHeading(
				('1' === count
					? intermediateResultsText[0]
					: intermediateResultsText[1]
				)
					.replace('%1', `<strong>${count}</strong>`)
					.replace('%2', `<strong>${count}</strong>`),
			);
		} else {
			setCardSummaryHeading(cardSummaryHeading);
		}
	}, [showFilter, state, cardsToDisplay, totalResults, cardSummaryHeading]);

	// This component could show cards that are passed in as a complete set, or as a subset.  If it's a complete
	// set, then we're going to wire up the filters to filter the cards.  If it's a subset, then we're going to
	// then we'll allow the higher order component that passes in the subset set the cards, which gets done via the
	// onShowMore callback.
	useEffect(() => {
		if (showFilter && state) {
			const filteredCards = (calianCareerCards || []).filter((card) => {
				for (let taxonomy in state) {
					if (
						!card.allTerms[taxonomy] ||
						card.allTerms[taxonomy].indexOf(
							state[taxonomy] as string,
						) === -1
					) {
						if (
							'unspecified' === state[taxonomy] &&
							!card.allTerms[taxonomy]
						) {
							// continuing, is when the user has selected unspecified, and the card doesn't have a value for the taxonomy
						} else {
							return false;
						}
					}
				}
				return true;
			});
			// Note, we're going to just show all filtered cards because a) it's what the user wants and b) pagination would
			// require yet another state variable, and I don't want to clutter the component any more.
			setCardsToDisplay(filteredCards);
		} else {
			setCardsToDisplay((calianCareerCards || []).slice(0, cardsToShow));
		}
	}, [showFilter, state, calianCareerCards]);

	const onClickShowMore = () => {
		if (onShowMore) {
			onShowMore();
		} else {
			setCardsToDisplay(
				(calianCareerCards || []).slice(
					0,
					cardsToDisplay.length + cardsToShow,
				),
			);
		}
	};
	const onClickTerm = (taxonomy: string, value: string) => {
		if (onClickCareerTerm) {
			onClickCareerTerm(taxonomy, value);
		} else if (filters) {
			let hasFilter = false;
			filters.forEach((filter) => {
				if (taxonomy === filter.taxonomy) {
					hasFilter = true;
				}
			});
			if (hasFilter) {
				updateState({ [taxonomy]: value });
			}
		}
	};

	return (
		<div
			className={
				className || cn('calian-career-card-group', ...classNames)
			}
			// If ID is not empty, add it to the component
			{...(cardGroupID && { id: cardGroupID })}
		>
			<div className="calian-career-card-group__container container-fluid">
				{heading && (
					<h2 className="calian-career-card-group__heading">
						{heading}
					</h2>
				)}
				{(showFilter || showResults) && (
					<div className="calian-career-card-group__filter-container container-fluid">
						{showFilter && filters?.length && (
							<div className="row">
								<div className="col-12">
									<form className="calian-career-card-group__filter">
										<div className="row">
											{filters.map((filter) => (
												<div
													className="form-group"
													key={filter.taxonomy}
												>
													<label
														htmlFor={`calian-career-card-group__filter-${filter.taxonomy}`}
														className="calian-career-card-group__filter-label visually-hidden"
													>
														{filter.label}
													</label>
													<Select
														className="form-select react-form-select"
														classNamePrefix="react-select"
														isSearchable
														isClearable
														id={`calian-career-card-group__filter-${filter.taxonomy}`}
														placeholder={
															filter.label
														}
														options={Object.values(
															filterOptions[
																filter.taxonomy
															],
														)}
														onChange={(o: any) => {
															updateState({
																[filter.taxonomy]:
																	o?.value,
															});
														}}
														aria-label={
															filter.label
														}
														filterOption={searchConfig as (option: any, rawInput: string) => boolean}
														value={
															filterOptions[
																filter.taxonomy
															][
																(state || {})[
																	filter
																		.taxonomy
																] || ''
															]
														}
													/>
												</div>
											))}
										</div>
										<input
											className="visually-hidden"
											type="submit"
											value="Submit"
										/>
									</form>
								</div>
							</div>
						)}
						{showResults && _cardSummaryHeading && !busy && (
							<div className="row">
								<div className="col-12">
									<p
										className="calian-career-card-group__filter-text"
										dangerouslySetInnerHTML={{
											__html: _cardSummaryHeading,
										}}
									/>
								</div>
							</div>
						)}
					</div>
				)}
				<div className="row">
					{busy ? (
						<Loading />
					) : (
						<>
							{cardsToDisplay?.length ? (
								<>
									<div className="calian-career-card-group__column col-12">
										<div className="calian-career-card-group__cards">
											{cardsToDisplay.map(
												(card, index) => (
													<div
														className="calian-career-card-group__card"
														key={index}
													>
														<CareerCard
															{...card}
															onClickTerm={
																onClickTerm
															}
														/>
													</div>
												),
											)}
										</div>
									</div>
									<div className="col-12">
										{!state &&
											cardsToDisplay.length <
												(totalResults ||
													calianCareerCards.length) && (
												<button
													type="button"
													aria-label=""
													className="calian-career-card-group__btn btn btn-primary"
													onClick={onClickShowMore}
													disabled={loadingMore}
												>
													{loadingMore ? (
														<Loading
															inline
															size={1}
															color="light"
														/>
													) : (
														loadMoreButtonText
													)}
												</button>
											)}
									</div>
								</>
							) : (
								<p className="calian-career-card-group__no-results text-center" dangerouslySetInnerHTML={{ __html: noResultsText }} />
							)}
						</>
					)}
				</div>
			</div>
		</div>
	);
};

export default CareerCardGroup;
